import { useSyncExternalStore } from 'react';
import {
  canAddInvite,
  type InviteChannel,
  type InviteDraft,
  type InviteRole,
  type PendingInvite,
} from '../domain/invites';

/**
 * Invite people › Ready to invite: the addresses a Group Leader adds one at a time before
 * sending them together, per group. Kept while the app runs, never stored: sending hands them
 * to Core (`useSendInvites`), which messages each one.
 */
const NONE: readonly InviteDraft[] = [];
let ready: Readonly<Record<string, readonly InviteDraft[]>> = {};
let inviteCount = 0;
const listeners = new Set<() => void>();

function setReady(groupId: string, next: readonly InviteDraft[]): void {
  ready = { ...ready, [groupId]: next };
  for (const listener of listeners) listener();
}

function readyFor(groupId: string): readonly InviteDraft[] {
  return ready[groupId] ?? NONE;
}

/** Ready to invite for `groupId`, outside React. */
export function getReadyInvites(groupId: string): readonly InviteDraft[] {
  return readyFor(groupId);
}

/**
 * Adds an address to Ready to invite, as a Member; ignored when already listed or among the
 * group's `pending` invitations.
 */
export function addReadyInvite(
  groupId: string,
  channel: InviteChannel,
  address: string,
  pending: readonly PendingInvite[] = [],
): void {
  if (!canAddInvite(address, readyFor(groupId), pending)) return;
  inviteCount += 1;
  const draft: InviteDraft = {
    id: `invite-${inviteCount}`,
    channel,
    address: address.trim(),
    role: 'member',
  };
  setReady(groupId, [...readyFor(groupId), draft]);
}

export function removeReadyInvite(groupId: string, inviteId: string): void {
  setReady(
    groupId,
    readyFor(groupId).filter((invite) => invite.id !== inviteId),
  );
}

/** Invite as: Member or Group leader. */
export function setReadyInviteRole(groupId: string, inviteId: string, role: InviteRole): void {
  setReady(
    groupId,
    readyFor(groupId).map((invite) => (invite.id === inviteId ? { ...invite, role } : invite)),
  );
}

export function clearReadyInvites(groupId: string): void {
  setReady(groupId, NONE);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Ready to invite for `groupId`, in the order added. */
export function useReadyInvites(groupId: string): readonly InviteDraft[] {
  const get = () => readyFor(groupId);
  return useSyncExternalStore(subscribe, get, get);
}
